import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, query, onSnapshot, addDoc, updateDoc, deleteDoc, doc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { OpsTask, OpsCategory, TaskStatus, TaskPriority, RecurFrequency, CRMUser, OpsComment } from '../types';
import AppHeader from '../components/ui/AppHeader';
import OpsSidebar from '../components/ops/OpsSidebar';
import OpsTable from '../components/ops/OpsTable';
import OpsKanban from '../components/ops/OpsKanban';
import OpsGantt from '../components/ops/OpsGantt';
import OpsTemplatesPane from '../components/ops/OpsTemplatesPane';
import OpsCommentsPopout from '../components/ops/OpsCommentsPopout';
import HubQuickSearch from '../components/search/HubQuickSearch';
import { useHubQuickSearchHotkey } from '../hooks/useHubQuickSearchHotkey';
import { searchOpsHub } from '../utils/hubSearch/searchOpsHub';
import { HubSearchResult } from '../types/hubSearch';
import { User, ChevronDown, X, Search, ListTodo, MessageSquare, ClipboardList } from 'lucide-react';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import { ACTION_REQUIRED_EXTERNAL_SENTINEL, isActionRequiredExternal } from '../utils/actionRequiredDisplay';
import { firestoreSnapshotListenError } from '../utils/firestoreListenDebug';
import { isAgentCreatedOpsTask } from '../utils/opsOriginDisplay';
import { OPS_VIEWER_LEAD_ID } from '../utils/opsAttachmentViewer';
import { filterOpsTaskEmployeeTeam } from '../utils/userRoles';
import { markOpsTaskNotificationsAsRead } from '../utils/notificationReadSync';
import { lastEditedStamp, recordCrmAuditAfterWrite, shouldWriteCrmAudit } from '../utils/crmAuditLog';
import { applyPlainCommentListKeydown } from '../utils/richCommentListShortcuts';

const PdfViewerModal = lazy(() => import('../components/pdf/PdfViewerModal'));
const ImageViewerModal = lazy(() => import('../components/pdf/ImageViewerModal'));
const CsvViewerModal = lazy(() => import('../components/csv/CsvViewerModal'));

type OpsFileViewerState = {
  url: string;
  title: string;
  documentId: string;
  taskId: string;
  mimeType?: string;
  annotationId?: string;
  pageNumber?: number;
};

type ViewMode = 'table' | 'board' | 'gantt' | 'templates';

const CATEGORY_LABELS: Record<OpsCategory, string> = {
  admin: 'Admin',
  maintenance: 'Maintenance',
  ordering: 'Ordering',
  requests: 'Requests',
  other: 'Other',
};

function computeNextDue(frequency: RecurFrequency, from: Date): Date {
  const d = new Date(from);
  switch (frequency) {
    case 'daily': d.setDate(d.getDate() + 1); break;
    case 'weekly': d.setDate(d.getDate() + 7); break;
    case 'biweekly': d.setDate(d.getDate() + 14); break;
    case 'monthly': d.setMonth(d.getMonth() + 1); break;
    case 'quarterly': d.setMonth(d.getMonth() + 3); break;
  }
  return d;
}

const OperationsHub: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightOpsCommentId, setHighlightOpsCommentId] = useState<string | null>(null);
  const [commentsPopoutTask, setCommentsPopoutTask] = useState<OpsTask | null>(null);
  const [showQuickSearch, setShowQuickSearch] = useState(false);
  const [opsCommentsForSearch, setOpsCommentsForSearch] = useState<OpsComment[]>([]);

  const [tasks, setTasks] = useState<OpsTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [selectedCategory, setSelectedCategory] = useState<OpsCategory | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [viewAsUser, setViewAsUser] = useState<string | null>(null);
  const [showViewAsPicker, setShowViewAsPicker] = useState(false);
  const [hideCompleted, setHideCompleted] = useState(true);
  const [showAgentTasksOnly, setShowAgentTasksOnly] = useState(false);
  const [opsPdfViewer, setOpsPdfViewer] = useState<OpsFileViewerState | null>(null);
  const [opsImageViewer, setOpsImageViewer] = useState<OpsFileViewerState | null>(null);
  const [opsCsvViewer, setOpsCsvViewer] = useState<OpsFileViewerState | null>(null);
  /** Apply "my tasks only" once per hub mount so choosing "Show All" is not overwritten. */
  const hasAppliedDefaultViewFilter = useRef(false);

  const activeTeam = useMemo(() => filterOpsTaskEmployeeTeam(crmUsers), [crmUsers]);

  const hubHeader = <AppHeader />;

  useEffect(() => {
    if (!user?.id) {
      hasAppliedDefaultViewFilter.current = false;
      setViewAsUser(null);
      return;
    }
    if (!hasAppliedDefaultViewFilter.current) {
      setViewAsUser(user.id);
      hasAppliedDefaultViewFilter.current = true;
    }
  }, [user?.id]);

  useEffect(() => {
    const q = query(collection(db, 'operationsTasks'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      snap => {
        const items: OpsTask[] = snap.docs.map(d => {
          const data = d.data();
          return {
            ...data,
            id: d.id,
            startDate: data.startDate?.toDate?.() || undefined,
            dueDate: data.dueDate?.toDate?.() || undefined,
            completedAt: data.completedAt?.toDate?.() || undefined,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.() || new Date(),
            recurring: data.recurring
              ? {
                  ...data.recurring,
                  nextDue: data.recurring.nextDue?.toDate?.() || null,
                }
              : undefined,
            subtasks: data.subtasks || [],
          } as OpsTask;
        });
        setTasks(items);
        setLoading(false);
      },
      err =>
        firestoreSnapshotListenError('OperationsHub', 'operationsTasks', err, {
          userId: user?.id,
          role: user?.role,
          email: user?.email,
        }),
    );
    return unsub;
  }, [user?.id, user?.role, user?.email]);

  /** Lazy-load ops comments while quick search is open (for comment body matching). */
  useEffect(() => {
    if (!showQuickSearch) {
      setOpsCommentsForSearch([]);
      return;
    }
    const q = query(collection(db, 'opsComments'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      snap => {
        const items: OpsComment[] = snap.docs.slice(0, 500).map(d => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.(),
          } as OpsComment;
        });
        setOpsCommentsForSearch(items);
      },
      err =>
        firestoreSnapshotListenError('OperationsHub', 'opsComments', err, {
          userId: user?.id,
          role: user?.role,
        }),
    );
    return unsub;
  }, [showQuickSearch, user?.id, user?.role]);

  /** Deep link: /operations?task=<id>&comment=<opsCommentDocId> (notifications, Slack, email) */
  useEffect(() => {
    const taskId = searchParams.get('task');
    const commentId = searchParams.get('comment');
    if (!taskId || tasks.length === 0) return;
    const t = tasks.find(x => x.id === taskId);
    if (!t) return;
    setCommentsPopoutTask(t);
    if (commentId) setHighlightOpsCommentId(commentId);
    // Drop query string so in-app task switching is not fighting the URL
    setSearchParams({}, { replace: true });
  }, [searchParams, tasks, setSearchParams]);

  /** Opening an ops task/comments thread clears related unread bell items (same pattern as Project Hub). */
  useEffect(() => {
    if (!user?.id || !commentsPopoutTask?.id) return;
    void markOpsTaskNotificationsAsRead(user.id, commentsPopoutTask.id);
  }, [user?.id, commentsPopoutTask?.id]);

  const filteredTasks = useMemo(() => {
    let result = tasks;

    if (hideCompleted) {
      result = result.filter(t => t.status !== 'completed');
    }

    if (selectedCategory) {
      result = result.filter(t => t.category === selectedCategory);
    }

    if (viewAsUser) {
      result = result.filter(t =>
        t.assignedTo?.includes(viewAsUser) ||
        t.actionRequired === viewAsUser ||
        t.collaborators?.includes(viewAsUser)
      );
    }

    if (showAgentTasksOnly) {
      result = result.filter(isAgentCreatedOpsTask);
    }

    return result;
  }, [tasks, selectedCategory, viewAsUser, hideCompleted, showAgentTasksOnly]);

  const categoryCounts = useMemo(() => {
    let source = tasks;
    if (hideCompleted) {
      source = source.filter(t => t.status !== 'completed');
    }
    if (viewAsUser) {
      source = source.filter(t =>
        t.assignedTo?.includes(viewAsUser) ||
        t.actionRequired === viewAsUser ||
        t.collaborators?.includes(viewAsUser)
      );
    }
    if (showAgentTasksOnly) {
      source = source.filter(isAgentCreatedOpsTask);
    }
    const counts: Record<string, number> = { all: source.length };
    for (const cat of ['admin', 'maintenance', 'ordering', 'requests', 'other'] as OpsCategory[]) {
      counts[cat] = source.filter(t => t.category === cat).length;
    }
    return counts;
  }, [tasks, viewAsUser, hideCompleted, showAgentTasksOnly]);

  const agentTaskCount = useMemo(() => {
    let source = tasks;
    if (hideCompleted) {
      source = source.filter(t => t.status !== 'completed');
    }
    if (viewAsUser) {
      source = source.filter(t =>
        t.assignedTo?.includes(viewAsUser) ||
        t.actionRequired === viewAsUser ||
        t.collaborators?.includes(viewAsUser)
      );
    }
    return source.filter(isAgentCreatedOpsTask).length;
  }, [tasks, viewAsUser, hideCompleted]);

  const hubTitle = useMemo(() => {
    const parts: string[] = [];
    if (showAgentTasksOnly) parts.push('Agent Tasks');
    if (selectedCategory) parts.push(CATEGORY_LABELS[selectedCategory]);
    return parts.length > 0 ? parts.join(' · ') : 'All Tasks';
  }, [showAgentTasksOnly, selectedCategory]);

  const tableEmptyMessage = useMemo(() => {
    if (filteredTasks.length > 0 || tasks.length === 0) return undefined;
    const parts: string[] = [];
    if (showAgentTasksOnly) parts.push('agent-sourced tasks');
    if (viewAsUser) parts.push('this person\'s tasks');
    if (selectedCategory) parts.push(`the ${CATEGORY_LABELS[selectedCategory]} category`);
    if (hideCompleted) parts.push('open tasks');
    if (parts.length === 0) return 'No tasks match the current filters.';
    return `No ${parts.join(' in ')} match the current filters. Try clearing filters or viewing all tasks.`;
  }, [filteredTasks.length, tasks.length, showAgentTasksOnly, viewAsUser, selectedCategory, hideCompleted]);

  const stats = useMemo(() => {
    const src = filteredTasks;
    const active = src.filter(t => t.status === 'in-progress').length;
    const overdue = src.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'completed').length;
    const completed = src.filter(t => t.status === 'completed').length;
    const actionItems = src.filter(t => t.actionRequired === (viewAsUser || user?.id)).length;
    return { total: src.length, active, overdue, completed, actionItems };
  }, [filteredTasks, viewAsUser, user?.id]);

  const opsTimelineScopeKey = useMemo(
    () =>
      `${viewAsUser ?? 'all'}:${selectedCategory ?? 'all'}:${hideCompleted ? 'open' : 'all'}:${showAgentTasksOnly ? 'agent' : 'all'}`,
    [viewAsUser, selectedCategory, hideCompleted, showAgentTasksOnly],
  );

  const createTask = useCallback(async (data: Partial<OpsTask>): Promise<string | undefined> => {
    if (!user) return undefined;
    const now = new Date();
    let assignedTo = data.assignedTo || [];
    // Quick-add and modal often omit assignee; default so tasks aren't hidden by "View As" filter.
    if (assignedTo.length === 0) {
      assignedTo = [viewAsUser || user.id];
    }
    const taskData: any = {
      title: data.title || 'Untitled Task',
      description: data.description || '',
      category: data.category || 'other',
      assignedTo,
      collaborators: data.collaborators || [],
      status: data.status || 'pending',
      priority: data.priority || 'medium',
      startDate: data.startDate ? Timestamp.fromDate(new Date(data.startDate)) : null,
      dueDate: data.dueDate ? Timestamp.fromDate(new Date(data.dueDate)) : null,
      estimatedHours: data.estimatedHours || null,
      recurring: data.recurring || null,
      requestedBy: data.requestedBy || '',
      actionRequired: data.actionRequired || null,
      actionRequiredNote: data.actionRequiredNote || null,
      subtasks: data.subtasks || [],
      tags: data.tags || [],
      ...(data.origin ? { origin: data.origin } : {}),
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
      completedAt: null,
      createdBy: user.id,
    };
    if (user?.id && shouldWriteCrmAudit({ uid: user.id, email: user.email, name: user.name })) {
      Object.assign(taskData, lastEditedStamp({ uid: user.id, email: user.email, name: user.name }));
    }
    const docRef = await addDoc(collection(db, 'operationsTasks'), taskData);
    if (user?.id) {
      void recordCrmAuditAfterWrite({
        actor: { uid: user.id, email: user.email, name: user.name },
        collection: 'operationsTasks',
        docId: docRef.id,
        action: 'create',
        entityType: 'opsTask',
        entityId: docRef.id,
        changedFields: Object.keys(taskData),
      });
    }
    setShowCreate(false);
    return docRef.id;
  }, [user, viewAsUser]);

  const updateTask = useCallback(async (id: string, updates: Partial<OpsTask>) => {
    if (updates.actionRequired && !isActionRequiredExternal(updates.actionRequired)) {
      const task = tasks.find(t => t.id === id);
      if (task) {
        const currentAssigned = updates.assignedTo || task.assignedTo || [];
        if (!currentAssigned.includes(updates.actionRequired)) {
          updates.assignedTo = [...currentAssigned, updates.actionRequired];
        }
      }
    }

    const ref = doc(db, 'operationsTasks', id);
    const payload: any = { ...updates, updatedAt: Timestamp.fromDate(new Date()) };

    // Sanitize: Firestore rejects undefined values
    Object.keys(payload).forEach(k => {
      if (payload[k] === undefined) payload[k] = null;
    });

    if (updates.startDate) payload.startDate = Timestamp.fromDate(new Date(updates.startDate));
    if (updates.dueDate) payload.dueDate = Timestamp.fromDate(new Date(updates.dueDate));
    if (updates.startDate === undefined && 'startDate' in updates) payload.startDate = null;
    if (updates.dueDate === undefined && 'dueDate' in updates) payload.dueDate = null;

    // Handle recurring: sanitize nested nextDue
    if (payload.recurring && typeof payload.recurring === 'object') {
      if (payload.recurring.nextDue === undefined) payload.recurring.nextDue = null;
      if (payload.recurring.nextDue instanceof Date) payload.recurring.nextDue = Timestamp.fromDate(payload.recurring.nextDue);
    }

    if (updates.status === 'completed') {
      payload.completedAt = Timestamp.fromDate(new Date());
      const task = tasks.find(t => t.id === id);
      if (task?.recurring) {
        const nextDue = computeNextDue(task.recurring.frequency, new Date());
        const nextStart = task.startDate ? computeNextDue(task.recurring.frequency, new Date(task.startDate)) : undefined;
        await addDoc(collection(db, 'operationsTasks'), {
          title: task.title,
          description: task.description || '',
          category: task.category,
          assignedTo: task.assignedTo,
          collaborators: task.collaborators || [],
          status: 'pending',
          priority: task.priority,
          startDate: nextStart ? Timestamp.fromDate(nextStart) : null,
          dueDate: Timestamp.fromDate(nextDue),
          estimatedHours: task.estimatedHours || null,
          recurring: { frequency: task.recurring.frequency, nextDue: Timestamp.fromDate(nextDue) },
          requestedBy: task.requestedBy || '',
          subtasks: (task.subtasks || []).map(s => ({ ...s, completed: false })),
          tags: task.tags || [],
          createdAt: Timestamp.fromDate(new Date()),
          updatedAt: Timestamp.fromDate(new Date()),
          completedAt: null,
          createdBy: task.createdBy,
          /** Distinguishes from user-created tasks so Cloud Functions Slack copy is not “New ops task”. */
          createdReason: 'recurring_next',
        });
      }
    }

    if (user?.id && shouldWriteCrmAudit({ uid: user.id, email: user.email, name: user.name })) {
      Object.assign(payload, lastEditedStamp({ uid: user.id, email: user.email, name: user.name }));
    }
    await updateDoc(ref, payload);
    if (user?.id) {
      void recordCrmAuditAfterWrite({
        actor: { uid: user.id, email: user.email, name: user.name },
        collection: 'operationsTasks',
        docId: id,
        action: 'update',
        entityType: 'opsTask',
        entityId: id,
        changedFields: Object.keys(updates),
      });
    }

    if (commentsPopoutTask?.id === id) {
      setCommentsPopoutTask(prev => prev ? { ...prev, ...updates } : null);
    }
  }, [tasks, commentsPopoutTask, user]);

  const deleteTask = useCallback(async (id: string) => {
    await deleteDoc(doc(db, 'operationsTasks', id));
    if (user?.id) {
      void recordCrmAuditAfterWrite({
        actor: { uid: user.id, email: user.email, name: user.name },
        collection: 'operationsTasks',
        docId: id,
        action: 'delete',
        entityType: 'opsTask',
        entityId: id,
      });
    }
    if (commentsPopoutTask?.id === id) setCommentsPopoutTask(null);
  }, [commentsPopoutTask, user]);

  const handleSelectTask = useCallback((task: OpsTask) => {
    setCommentsPopoutTask(task);
  }, []);

  const handleCloseOpsCommentsPopout = useCallback(() => {
    setCommentsPopoutTask(null);
    setHighlightOpsCommentId(null);
  }, []);

  const openOpsPdf = useCallback((
    url: string,
    title: string,
    documentId: string,
    opsTaskId: string,
    opts?: { annotationId?: string; pageNumber?: number },
  ) => {
    setOpsPdfViewer({
      url,
      title,
      documentId,
      taskId: opsTaskId,
      annotationId: opts?.annotationId,
      pageNumber: opts?.pageNumber,
    });
  }, []);

  const openOpsImage = useCallback((
    url: string,
    title: string,
    documentId: string,
    opsTaskId: string,
    opts?: { annotationId?: string; mimeType?: string },
  ) => {
    setOpsImageViewer({
      url,
      title,
      documentId,
      taskId: opsTaskId,
      mimeType: opts?.mimeType,
      annotationId: opts?.annotationId,
    });
  }, []);

  const openOpsCsv = useCallback((
    url: string,
    title: string,
    documentId: string,
    opsTaskId: string,
    opts?: { annotationId?: string },
  ) => {
    setOpsCsvViewer({
      url,
      title,
      documentId,
      taskId: opsTaskId,
      annotationId: opts?.annotationId,
    });
  }, []);

  const getOpsQuickSearchResults = useCallback(
    (query: string) =>
      searchOpsHub({
        query,
        opsTasks: tasks,
        crmUsers,
        opsComments: opsCommentsForSearch,
      }),
    [tasks, crmUsers, opsCommentsForSearch],
  );

  const handleOpsQuickSearchSelect = useCallback(
    (result: HubSearchResult) => {
      if (result.kind === 'opsTask') {
        setCommentsPopoutTask(result.payload as OpsTask);
        return;
      }
      if (result.kind === 'opsComment') {
        const { comment, task } = result.payload as { comment: OpsComment; task?: OpsTask };
        const t = task || tasks.find(x => x.id === comment.taskId);
        if (t) {
          setCommentsPopoutTask(t);
          if (comment.id) setHighlightOpsCommentId(comment.id);
        }
      }
    },
    [tasks],
  );

  useHubQuickSearchHotkey({
    onOpen: () => setShowQuickSearch(true),
    blocked: showQuickSearch || showCreate || !!commentsPopoutTask,
  });

  const freshCommentsPopoutTask = useMemo(() => {
    if (!commentsPopoutTask?.id) return commentsPopoutTask;
    return tasks.find(t => t.id === commentsPopoutTask.id) || commentsPopoutTask;
  }, [commentsPopoutTask, tasks]);

  const viewAsName = viewAsUser ? activeTeam.find(u => u.uid === viewAsUser)?.displayName : null;

  const getInitials = (name: string) => {
    const p = name.split(' ');
    return p.length >= 2 ? `${p[0][0]}${p[1][0]}` : name.substring(0, 2);
  };

  if (loading) {
    return (
      <div className="ops-hub">
        {hubHeader}
        <div className="ops-hub-loading">
          <div className="ops-hub-loading-bar" />
        </div>
      </div>
    );
  }

  return (
    <div className="ops-hub">
      {hubHeader}
      <div className="ops-hub-layout">
        <OpsSidebar
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
          categoryCounts={categoryCounts}
          onNewTask={() => setShowCreate(true)}
          hideCompleted={hideCompleted}
          onToggleHideCompleted={() => setHideCompleted(h => !h)}
          showAgentTasksOnly={showAgentTasksOnly}
          onToggleAgentTasksOnly={() => setShowAgentTasksOnly(v => !v)}
          agentTaskCount={agentTaskCount}
        />

        <div className="ops-hub-main">
          {/* Toolbar */}
          <div className="ops-hub-toolbar">
            <div className="ops-hub-toolbar-left">
              <h2 className="ops-hub-title">
                {hubTitle}
              </h2>
              <div className="ops-hub-stats">
                <span className="ops-stat">{stats.total} total</span>
                {stats.active > 0 && <span className="ops-stat ops-stat-active">{stats.active} active</span>}
                {stats.overdue > 0 && <span className="ops-stat ops-stat-overdue">{stats.overdue} overdue</span>}
                {stats.actionItems > 0 && <span className="ops-stat ops-stat-action">{stats.actionItems} action</span>}
                <span className="ops-stat ops-stat-done">{stats.completed} done</span>
              </div>
            </div>
            <div className="ops-hub-toolbar-right">
              {/* View as user */}
              <div className="ops-view-as-container" style={{ position: 'relative' }}>
                {viewAsUser ? (
                  <div className="ops-view-as-active">
                    <button type="button" className="ops-view-as-active-main" onClick={() => setShowViewAsPicker(!showViewAsPicker)}>
                      <span className="ops-view-as-avatar">{getInitials(viewAsName || '')}</span>
                      {viewAsName?.split(' ')[0]}'s Tasks
                    </button>
                    <button type="button" className="ops-view-as-clear" onClick={() => { setViewAsUser(null); setShowViewAsPicker(false); }} aria-label="Clear view-as filter">
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <button className="ops-view-as-btn" onClick={() => setShowViewAsPicker(!showViewAsPicker)}>
                    <User size={13} /> View As <ChevronDown size={12} />
                  </button>
                )}
                {showViewAsPicker && (
                  <div className="ops-view-as-dropdown">
                    {viewAsUser && (
                      <button className="ops-view-as-option" onClick={() => { setViewAsUser(null); setShowViewAsPicker(false); }}>
                        <span className="ops-view-as-option-label">Show All</span>
                      </button>
                    )}
                    {activeTeam.map(u => (
                      <button
                        key={u.uid}
                        className={`ops-view-as-option ${viewAsUser === u.uid ? 'selected' : ''}`}
                        onClick={() => { setViewAsUser(u.uid); setShowViewAsPicker(false); }}
                      >
                        <span className="ops-view-as-option-avatar">{getInitials(u.displayName)}</span>
                        <span className="ops-view-as-option-label">{u.displayName}</span>
                        {u.uid === user?.id && <span className="ops-view-as-you">(you)</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="ops-hub-views">
                <button className={`ops-view-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                  Table
                </button>
                <button className={`ops-view-btn ${viewMode === 'board' ? 'active' : ''}`} onClick={() => setViewMode('board')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                  Board
                </button>
                <button className={`ops-view-btn ${viewMode === 'gantt' ? 'active' : ''}`} onClick={() => setViewMode('gantt')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="6" x2="16" y2="6"/><line x1="8" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="14" y2="18"/></svg>
                  Timeline
                </button>
                <button className={`ops-view-btn ${viewMode === 'templates' ? 'active' : ''}`} onClick={() => setViewMode('templates')}>
                  <ClipboardList size={14} />
                  Templates
                </button>
              </div>
              <button className="ops-new-btn" onClick={() => setShowCreate(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New Task
              </button>
              <button
                type="button"
                className="ops-view-btn"
                onClick={() => setShowQuickSearch(true)}
                title="Quick search (Space)"
                aria-label="Quick search ops tasks and comments"
              >
                <Search size={14} />
              </button>
            </div>
          </div>

          {/* Main content */}
          <div className="ops-hub-content">
            <div className={`ops-view-pane ${viewMode === 'table' ? 'active' : ''}`}>
              <OpsTable
                tasks={filteredTasks}
                teamMembers={crmUsers}
                onSelectTask={handleSelectTask}
                onUpdateTask={updateTask}
                onCreateTask={createTask}
                emptyFilteredMessage={tableEmptyMessage}
              />
            </div>
            <div className={`ops-view-pane ${viewMode === 'board' ? 'active' : ''}`}>
              <OpsKanban
                tasks={filteredTasks}
                teamMembers={crmUsers}
                onSelectTask={handleSelectTask}
                onUpdateTask={updateTask}
              />
            </div>
            <div className={`ops-view-pane ${viewMode === 'gantt' ? 'active' : ''}`}>
              <OpsGantt
                key={opsTimelineScopeKey}
                tasks={filteredTasks}
                teamMembers={crmUsers}
                timelineScopeKey={opsTimelineScopeKey}
                onSelectTask={handleSelectTask}
                onUpdateTask={updateTask}
              />
            </div>
            <div className={`ops-view-pane ${viewMode === 'templates' ? 'active' : ''}`}>
              <OpsTemplatesPane
                teamMembers={crmUsers}
                onCreateTask={createTask}
                onOpenTask={handleSelectTask}
                onFinished={() => setViewMode('table')}
              />
            </div>
          </div>
        </div>
      </div>

      <OpsCommentsPopout
        task={freshCommentsPopoutTask}
        teamMembers={crmUsers}
        highlightCommentId={highlightOpsCommentId}
        onHighlightCommentConsumed={() => setHighlightOpsCommentId(null)}
        onUpdate={updateTask}
        onDelete={deleteTask}
        onClose={handleCloseOpsCommentsPopout}
        onOpenPdf={openOpsPdf}
        onOpenImage={openOpsImage}
        onOpenCsv={openOpsCsv}
      />

      {opsPdfViewer && (
        <Suspense fallback={null}>
          <PdfViewerModal
            key={`${opsPdfViewer.documentId}:${opsPdfViewer.annotationId ?? ''}:${opsPdfViewer.pageNumber ?? ''}`}
            url={opsPdfViewer.url}
            title={opsPdfViewer.title}
            documentId={opsPdfViewer.documentId}
            leadId={OPS_VIEWER_LEAD_ID}
            taskId={opsPdfViewer.taskId}
            disableTakeoff
            initialAnnotationId={opsPdfViewer.annotationId}
            initialPageNumber={opsPdfViewer.pageNumber}
            onClose={() => setOpsPdfViewer(null)}
          />
        </Suspense>
      )}
      {opsImageViewer && (
        <Suspense fallback={null}>
          <ImageViewerModal
            url={opsImageViewer.url}
            title={opsImageViewer.title}
            documentId={opsImageViewer.documentId}
            leadId={OPS_VIEWER_LEAD_ID}
            taskId={opsImageViewer.taskId}
            mimeType={opsImageViewer.mimeType}
            initialAnnotationId={opsImageViewer.annotationId}
            onClose={() => setOpsImageViewer(null)}
          />
        </Suspense>
      )}
      {opsCsvViewer && (
        <Suspense fallback={null}>
          <CsvViewerModal
            url={opsCsvViewer.url}
            title={opsCsvViewer.title}
            documentId={opsCsvViewer.documentId}
            leadId={OPS_VIEWER_LEAD_ID}
            taskId={opsCsvViewer.taskId}
            initialAnnotationId={opsCsvViewer.annotationId}
            onClose={() => setOpsCsvViewer(null)}
          />
        </Suspense>
      )}

      <HubQuickSearch
        open={showQuickSearch}
        onClose={() => setShowQuickSearch(false)}
        getResults={getOpsQuickSearchResults}
        onSelect={handleOpsQuickSearchSelect}
        placeholder="Search ops tasks, assignees, comments…"
        ariaLabel="Operations quick search"
        renderIcon={(result) =>
          result.kind === 'opsComment' ? <MessageSquare size={16} /> : <ListTodo size={16} />
        }
      />

      {/* Quick-create modal */}
      {showCreate && (
        <QuickCreateModal
          teamMembers={crmUsers}
          currentUserId={user?.id || ''}
          currentUserName={crmUsers.find(u => u.uid === user?.id)?.displayName || user?.email || ''}
          onSubmit={createTask}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
};

/* ── Quick Create Modal ── */

const QuickCreateModal: React.FC<{
  teamMembers: CRMUser[];
  currentUserId: string;
  currentUserName: string;
  onSubmit: (data: Partial<OpsTask>) => Promise<string | undefined>;
  onClose: () => void;
}> = ({ teamMembers, currentUserId, currentUserName, onSubmit, onClose }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [initialComment, setInitialComment] = useState('');
  const [category, setCategory] = useState<OpsCategory>('admin');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [assignee, setAssignee] = useState('');
  const [actionRequired, setActionRequired] = useState('');
  const [actionRequiredExternalText, setActionRequiredExternalText] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [collaborators, setCollaborators] = useState<string[]>([]);
  /** '' = no estimate; qtr/half/one = presets; custom = use customHours */
  const [estimatePreset, setEstimatePreset] = useState<'' | 'qtr' | 'half' | 'one' | 'custom'>('');
  const [customHours, setCustomHours] = useState('');
  const [showCollabPicker, setShowCollabPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const activeTeam = filterOpsTaskEmployeeTeam(teamMembers);

  const getInitials = (name: string) => {
    const p = name.split(' ');
    return p.length >= 2 ? `${p[0][0]}${p[1][0]}` : name.substring(0, 2);
  };

  const toggleCollaborator = (uid: string) => {
    setCollaborators(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const availableCollabs = filterOpsTaskEmployeeTeam(teamMembers).filter(u => u.uid !== assignee);

  const resolveEstimateHours = (): number | undefined => {
    if (estimatePreset === 'qtr') return 0.25;
    if (estimatePreset === 'half') return 0.5;
    if (estimatePreset === 'one') return 1;
    if (estimatePreset === 'custom') {
      const v = parseFloat(customHours);
      if (!Number.isFinite(v) || v <= 0) return undefined;
      return Math.max(0.25, Math.round(v * 4) / 4);
    }
    return undefined;
  };

  const roundCustomToQuarter = (raw: string): string => {
    const v = parseFloat(raw);
    if (!Number.isFinite(v) || v <= 0) return '';
    const q = Math.max(0.25, Math.round(v * 4) / 4);
    return String(q);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    try {
      const hours = resolveEstimateHours();
      let ar: string | undefined;
      let arNote: string | undefined;
      if (actionRequired === '__external__') {
        const ext = actionRequiredExternalText.trim();
        if (ext) {
          ar = ACTION_REQUIRED_EXTERNAL_SENTINEL;
          arNote = ext;
        }
      } else if (actionRequired) {
        ar = actionRequired;
      }

      const taskId = await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        category,
        priority,
        status: 'pending' as TaskStatus,
        assignedTo: assignee ? [assignee] : [],
        collaborators: collaborators.length > 0 ? collaborators : [],
        actionRequired: ar,
        actionRequiredNote: arNote,
        startDate: new Date(),
        dueDate: dueDate ? new Date(dueDate + 'T00:00:00') : undefined,
        estimatedHours: hours && hours > 0 ? hours : undefined,
      });

      if (initialComment.trim() && taskId) {
        await addDoc(collection(db, 'opsComments'), {
          taskId,
          userId: currentUserId,
          userName: currentUserName,
          userInitials: getInitials(currentUserName),
          text: initialComment.trim(),
          mentions: null,
          attachments: null,
          createdAt: Timestamp.now(),
        });
      }
    } catch (err) {
      console.error('Error creating ops task:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ops-modal-overlay" onClick={onClose}>
      <div className="ops-modal" onClick={e => e.stopPropagation()}>
        <div className="ops-modal-header">
          <h3>New Task</h3>
          <button className="ops-modal-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="ops-modal-body">
            <input
              className="ops-modal-input ops-modal-input-title"
              placeholder="Task title..."
              value={title}
              onChange={e => setTitle(e.target.value)}
              autoFocus
            />

            <textarea
              className="ops-modal-textarea"
              placeholder="Description (optional)..."
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
            />

            <div className="ops-modal-grid">
              <div className="ops-modal-row">
                <label className="ops-modal-label">Category</label>
                <select className="ops-modal-select" value={category} onChange={e => setCategory(e.target.value as OpsCategory)}>
                  <option value="admin">Admin</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="ordering">Ordering</option>
                  <option value="requests">Requests</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="ops-modal-row">
                <label className="ops-modal-label">Priority</label>
                <select className="ops-modal-select" value={priority} onChange={e => setPriority(e.target.value as TaskPriority)}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div className="ops-modal-row">
              <label className="ops-modal-label">Assignee</label>
              <select className="ops-modal-select" value={assignee} onChange={e => setAssignee(e.target.value)}>
                <option value="">Unassigned</option>
                {activeTeam.map(u => (
                  <option key={u.uid} value={u.uid}>{u.displayName}</option>
                ))}
              </select>
            </div>

            <div className="ops-modal-row">
              <label className="ops-modal-label">Action Required</label>
              <select
                className="ops-modal-select"
                value={actionRequired}
                onChange={e => {
                  setActionRequired(e.target.value);
                  if (e.target.value !== '__external__') setActionRequiredExternalText('');
                }}
              >
                <option value="">None</option>
                {activeTeam.map(u => (
                  <option key={u.uid} value={u.uid}>{u.displayName}</option>
                ))}
                <option value="__external__">External (specify who)…</option>
              </select>
            </div>
            {actionRequired === '__external__' && (
              <div className="ops-modal-row">
                <label className="ops-modal-label">Waiting on</label>
                <input
                  className="ops-modal-input"
                  placeholder="e.g. county inspector, vendor"
                  value={actionRequiredExternalText}
                  onChange={e => setActionRequiredExternalText(e.target.value)}
                />
              </div>
            )}

            <div className="ops-modal-row">
              <label className="ops-modal-label">Deadline</label>
              <input
                type="date"
                className="ops-modal-date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
              />
            </div>

            <div className="ops-modal-row ops-modal-row--estimate">
              <label className="ops-modal-label">Estimate</label>
              <div className="ops-modal-estimate-block">
                <div className="ops-estimate-chips" role="group" aria-label="Time estimate">
                  {([
                    { id: 'qtr' as const, label: '¼ hr' },
                    { id: 'half' as const, label: '½ hr' },
                    { id: 'one' as const, label: '1 hr' },
                  ]).map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      className={`ops-estimate-chip ${estimatePreset === id ? 'active' : ''}`}
                      aria-pressed={estimatePreset === id}
                      onClick={() => {
                        setEstimatePreset(prev => (prev === id ? '' : id));
                        setCustomHours('');
                      }}
                    >
                      {label}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`ops-estimate-chip ${estimatePreset === 'custom' ? 'active' : ''}`}
                    aria-pressed={estimatePreset === 'custom'}
                    onClick={() => {
                      setEstimatePreset(prev => (prev === 'custom' ? '' : 'custom'));
                    }}
                  >
                    Custom
                  </button>
                </div>
                {estimatePreset === 'custom' && (
                  <div className="ops-estimate-custom">
                    <input
                      type="number"
                      className="ops-modal-input ops-modal-input-hours"
                      placeholder="1.75"
                      title="Enter hours in steps of 0.25 (valid: 1.25, 1.5, 1.75, 2, …)"
                      min={0.25}
                      step={0.25}
                      value={customHours}
                      onChange={e => setCustomHours(e.target.value)}
                      onBlur={() => setCustomHours(prev => roundCustomToQuarter(prev))}
                    />
                    <span className="ops-modal-estimate-unit">hours (0.25 hr steps — e.g. 1.25, 1.5, 1.75)</span>
                  </div>
                )}
              </div>
            </div>

            <div className="ops-modal-section">
              <label className="ops-modal-label">Collaborators</label>
              <div className="ops-modal-collabs">
                {collaborators.map(uid => {
                  const u = teamMembers.find(m => m.uid === uid);
                  if (!u) return null;
                  return (
                    <span key={uid} className="ops-modal-collab-chip" onClick={() => toggleCollaborator(uid)}>
                      <span className="ops-modal-collab-avatar">{getInitials(u.displayName)}</span>
                      {u.displayName}
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </span>
                  );
                })}
                <button type="button" className="ops-modal-collab-add" onClick={() => setShowCollabPicker(!showCollabPicker)}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add people
                </button>
                {showCollabPicker && (
                  <div className="ops-modal-collab-dropdown">
                    {availableCollabs.length === 0 && <div className="ops-modal-collab-empty">No team members available</div>}
                    {availableCollabs.map(u => (
                      <button
                        key={u.uid}
                        type="button"
                        className={`ops-modal-collab-option ${collaborators.includes(u.uid) ? 'selected' : ''}`}
                        onClick={() => { toggleCollaborator(u.uid); setShowCollabPicker(false); }}
                      >
                        <span className="ops-modal-collab-avatar">{getInitials(u.displayName)}</span>
                        {u.displayName}
                        {collaborators.includes(u.uid) && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ marginLeft: 'auto' }}><polyline points="20 6 9 17 4 12"/></svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="ops-modal-section">
              <label className="ops-modal-label">Initial Comment</label>
              <textarea
                className="ops-modal-textarea"
                placeholder="Add a comment that will post to the thread..."
                value={initialComment}
                onChange={e => setInitialComment(e.target.value)}
                onKeyDown={e => applyPlainCommentListKeydown(e, e.currentTarget, setInitialComment, 'enter')}
                rows={2}
              />
            </div>
          </div>
          <div className="ops-modal-footer">
            <button type="button" className="pm-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="pm-btn pm-btn-primary" disabled={!title.trim() || submitting}>
              {submitting ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OperationsHub;
